import React, { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { useRemoteStrings } from '@acorns/web-utils'

import { Performance as PerformanceType } from 'generated/types'
import useAnalyticsComponentViewed from 'hooks/useAnalyticsComponentViewed'
import {
  PERFORMANCE_CONTENT,
  PERFORMANCE_DETAILS,
  PERFORMANCE_GAIN_LOSS,
  PERFORMANCE_SECTION,
} from 'utils/testing/testIDs'

import { OPTIONS, SECTION_NAMES, baseAnalyticsOptions } from '../analytics'
import { ArticleButton } from '../shared/ArticleButton/ArticleButton'
import {
  Container,
  Content,
  DetailContainer,
  InteractiveBackground,
  SectionIcon,
  Title,
} from '../shared/SectionDetails'
import { ArrowToggle } from './ArrowToggle'
import { Chart } from './Chart/Chart'
import { ChartLabel } from './Chart/ChartLabel'
import { GainLoss } from './GainLoss'
import { StickWithItModal } from './StickWithItModal'
import { GainLossWrapper } from './styled-components'

const Performance = ({
  accountValues,
  beneficiaryId,
  marketChange,
  marketChangePercent,
  marketChangePercentWithoutFees,
  marketChangeWithoutFees,
  endsAt,
  startsAt,
}: {
  beneficiaryId: string
  endsAt: string
  startsAt: string
} & PerformanceType): React.ReactElement => {
  const { getRemoteString } = useRemoteStrings()
  const inViewContainerRef = useAnalyticsComponentViewed({
    action: 'Viewed',
    payload: {
      ...baseAnalyticsOptions,
      ...OPTIONS.PERFORMANCE,
      beneficiaryId,
      event_description: 'Early Quarterly Recaps Performance Section Viewed',
      object: 'Screen',
      object_name: 'earlyQuarterlyRecapsPerformanceSection',
    },
  })
  const { ref, inView } = useInView({
    threshold: 1,
    triggerOnce: true,
  })

  const [isMarket, setMarket] = useState(true) // market or total
  const [xPos, setXPos] = useState<number | null>(null)
  const chartRef = useRef<HTMLDivElement | null>(null)

  const [accountValue, setAccountValue] = useState(
    accountValues?.[accountValues.length - 1]?.amount || 0,
  )

  const [hasShownModal, setHasShownModal] = useState(false)
  const [showModal, setShowModal] = useState(false)

  const changeAmount = isMarket ? marketChange : marketChangeWithoutFees
  const isUp = Math.sign(changeAmount || 0) > 0

  useEffect(() => {
    if (inView && !isUp && !hasShownModal) {
      setHasShownModal(true)
      setShowModal(true)
    }
  }, [hasShownModal, inView, isUp])

  const handleTouchStart = (evt: React.TouchEvent) => {
    setXPos(evt.touches[0].clientX)
  }

  const handleTouchMove = (evt: React.TouchEvent) => {
    const newX = evt.touches[0].clientX

    if (xPos !== null) {
      setMarket(newX > xPos)
    }
  }

  const handleMouseDown = (evt: React.MouseEvent) => {
    setXPos(evt.clientX)
  }

  const handleMouseMove = (evt: React.MouseEvent) => {
    const newX = evt.clientX

    if (xPos !== null) {
      setMarket(newX > xPos)
    }
  }

  const handleActionEnd = () => {
    setXPos(null)
  }

  const title = getRemoteString({
    key: isUp
      ? 'early.recap.performance.positive_gain.header'
      : 'early.recap.performance.negative_gain.header',
  })

  return (
    <Container data-testid={PERFORMANCE_SECTION} ref={inViewContainerRef}>
      <StickWithItModal
        isOpen={showModal}
        onDismiss={() => setShowModal(false)}
      />
      <Content detailOnRight data-testid={PERFORMANCE_DETAILS} ref={ref}>
        <ArticleButton
          beneficiaryId={beneficiaryId}
          section={SECTION_NAMES.PERFORMANCE}
        />
        <InteractiveBackground ref={chartRef}>
          <Chart
            accountValues={accountValues}
            onSetAccountValue={setAccountValue}
          />
          <ChartLabel startsAt={startsAt} endsAt={endsAt} />
        </InteractiveBackground>
        <DetailContainer detailOnRight data-testid={PERFORMANCE_CONTENT}>
          <SectionIcon sectionIndex={2} />
          <Title>{title}</Title>
          <GainLossWrapper
            data-testid={PERFORMANCE_GAIN_LOSS}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleActionEnd}
            onTouchEnd={handleActionEnd}
            onTouchMove={handleTouchMove}
            onTouchStart={handleTouchStart}
          >
            <GainLoss
              accountValue={accountValue}
              isMarket={isMarket}
              marketChange={marketChange || 0}
              marketPercentChange={marketChangePercent || 0}
              totalChange={marketChangeWithoutFees || 0}
              totalPercentChange={marketChangePercentWithoutFees || 0}
            />
          </GainLossWrapper>
          <ArrowToggle isMarket={isMarket} onClickMarket={setMarket} />
        </DetailContainer>
      </Content>
    </Container>
  )
}

export { Performance }
